// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Dialog} from '@deere/isg.component-library';

const PREVIEW_WIDTH = 136;
const PREVIEW_HEIGHT = 100;
const ROW_HEIGHT = 15;
const WORKSHEET_COLUMN_WIDTH = 60;
const MAX_PREVIEW_ROWS = 12;

function getThumbnailRows(rows) {
    if (rows.length <= MAX_PREVIEW_ROWS) {
        return rows;
    }

    return [
        rows[0],
        ...rows.slice(1, MAX_PREVIEW_ROWS - 1),
        rows[rows.length - 1]
    ];
}

function getPreviewScale(rowCount, columnCount) {
    if (!rowCount) {
        return 1;
    }

    const worksheetWidth = columnCount * WORKSHEET_COLUMN_WIDTH;

    return Math.min(1, PREVIEW_WIDTH / worksheetWidth, PREVIEW_HEIGHT / (rowCount * ROW_HEIGHT));
}

function LaborReportExportDialog(props) {
    const {
        dateRange,
        onCancel,
        onDownload,
        rows,
        translations
    } = props;
    const thumbnailRows = getThumbnailRows(rows);
    const columnCount = thumbnailRows.reduce((maxColumnCount, row) => Math.max(maxColumnCount, row.length), 1);
    const previewScale = getPreviewScale(thumbnailRows.length, columnCount);

    return (
        <Dialog
            className='labor-report-export-dialog'
            closeHandler={onCancel}
            footerActions={[
                {
                    onClick: onCancel,
                    title: translations.CANCEL,
                    variant: 'secondary'
                },
                {
                    onClick: onDownload,
                    title: translations.DOWNLOAD,
                    variant: 'primary'
                }
            ]}
            open={true}
            title={translations.EXPORT}
            width='sm'
        >
            <div className='labor-report-export-content'>
                <div className='labor-report-export-details'>
                    <p className='labor-report-export-description'>{translations.ONLINK_EXPORT_CURRENT_VIEW_DESCRIPTION}</p>
                    <div className='labor-report-export-timeframe-label'>{translations.ONLINK_SELECTED_TIMEFRAME}</div>
                    <div className='labor-report-export-timeframe'>{dateRange}</div>
                </div>
                <div
                    aria-label='Spreadsheet preview'
                    className='labor-report-export-preview'
                    role='group'
                >
                    <table
                        className='labor-report-export-preview-sheet'
                        style={{
                            transform: `scale(${previewScale})`,
                            width: `${columnCount * WORKSHEET_COLUMN_WIDTH}px`
                        }}
                    >
                        <tbody>
                            {thumbnailRows.map((row, index) => (
                                <tr
                                    className={index === 0 || index === thumbnailRows.length - 1 ? 'emphasized-row' : ''}
                                    key={`${index}-${row[0]}`}
                                >
                                    {row.map((value, cellIndex) => (
                                        <td key={`${index}-${cellIndex}`}>{value}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Dialog>
    );
}

LaborReportExportDialog.propTypes = {
    dateRange: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.array).isRequired,
    translations: PropTypes.translations.isRequired
};

export default LaborReportExportDialog;
